import React from 'react';
import PorchpairIcon from '../images/PorchpairIcon.png';
import porchpairLanding from '../images/porchpair_landing.jpg';
import image1 from '../images/porchpair1.png';
import image2 from '../images/porchpair2.png';
import image3 from '../images/porchpair3.png';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400;1,500&family=DM+Sans:wght@300;400;500&display=swap');

  :root {
    --ink:       #1A1814;
    --ink-2:     #4A4740;
    --ink-3:     #8A8780;
    --rule:      rgba(26,24,20,0.10);
    --rule-md:   rgba(26,24,20,0.18);
    --sand:      #F7F4EE;
    --white:     #FFFFFF;
    --green:     #1C4717;
    --green-lt:  #EDF3EC;
    --serif:     'Cormorant Garamond', Georgia, serif;
    --sans:      'DM Sans', system-ui, sans-serif;
  }

  /* ── HERO ─────────────────────────────────────────────────────────────────── */
  .pp-hero {
    position: relative;
    width: 100%;
    height: 70vh;
    overflow: hidden;
  }

  .pp-hero-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .pp-hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.08), rgba(0,0,0,0.45));
  }

  .pp-hero-label {
    position: absolute;
    bottom: 48px;
    left: 48px;
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .pp-hero-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    object-fit: contain;
    background: var(--white);
    padding: 4px;
  }

  .pp-hero-title {
    font-family: var(--serif);
    font-size: 56px;
    font-weight: 400;
    color: var(--white);
    line-height: 1;
    letter-spacing: 0.01em;
  }

  /* ── BODY ─────────────────────────────────────────────────────────────────── */
  .pp-body {
    display: grid;
    grid-template-columns: 1fr 580px;
    min-height: 600px;
  }

  /* ── LEFT: TEXT ───────────────────────────────────────────────────────────── */
  .pp-text {
    padding: 64px 48px 75px 100px;
    border-right: 0.5px solid var(--rule);
  }

  .pp-text p {
    font-family: var(--sans);
    font-size: 25px;
    font-weight: 300;
    line-height: 1.85;
    color: var(--ink);
    margin-bottom: 28px;
    max-width: 590px;
    font-weight: 400;
    font-size: 17px;
  }

  .pp-text p:last-child { margin-bottom: 0; }

  /* ── RIGHT: COLLAGE ───────────────────────────────────────────────────────── */
  .pp-collage {
    position: relative;
    background: var(--sand);
    overflow: hidden;
    min-height: 900px;
  }

  .pp-collage-img {
    position: absolute;
    object-fit: cover;
    border-radius: 8px;
    outline: 2px solid var(--white);
    box-shadow: 0 4px 24px rgba(0,0,0,0.10);
  }

  .pp-collage-img:nth-child(1) {
    width: 230px;
    height: 320px;
    top: 60px;
    left: 50%;
    transform: translateX(-80%);
  }

  .pp-collage-img:nth-child(2) {
    width: 215px;
    height: 305px;
    top: 240px;
    left: 50%;
    transform: translateX(-25%);
  }

  .pp-collage-img:nth-child(3) {
    width: 220px;
    height: 310px;
    top: 500px;
    left: 50%;
    transform: translateX(-80%);
  }

  /* ── RESPONSIVE ──────────────────────────────────────────────────────────── */
  @media (max-width: 900px) {
    .pp-hero-title { font-size: 36px; }
    .pp-hero-label { left: 24px; bottom: 32px; }
    .pp-body { grid-template-columns: 1fr; }
    .pp-text { padding: 48px 24px; border-right: none; }
    .pp-collage { min-height: 600px; }
    .pp-collage-img:nth-child(1),
    .pp-collage-img:nth-child(2),
    .pp-collage-img:nth-child(3) { left: 50%; }
  }
`;

function PorchpairPage() {
  return (
    <div>
      <style>{styles}</style>

      {/* ── HERO ── */}
      <div className="pp-hero">
        <img src={porchpairLanding} alt="Porchpair" className="pp-hero-img" />
        <div className="pp-hero-overlay" />
        <div className="pp-hero-label">
          <img src={PorchpairIcon} alt="Porchpair" className="pp-hero-icon" />
          <span className="pp-hero-title">Porchpair</span>
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="pp-body">
        <div className="pp-text">
          <p>Package theft costs Americans over $8 billion a year, and when I moved to Baltimore I became part of that number. I started having packages stolen regularly and couldn't find a good solution — so I built one.</p>

          <p>Porchpair connects residents with nearby local businesses who agree to receive packages on their behalf for a small fee. It's a simple idea but it meant navigating a two-sided marketplace from scratch, finding businesses willing to participate, and finding customers who knew the service existed. Without providers there's no service, and without customers there's no reason for a provider to sign up. I focused on the provider side first, walking door to door through dozens of local Baltimore businesses until High Grounds Cafe agreed to be our first partner. Once that was in place, we put up flyers in-store, ran a promotional event, and distributed door hangers in the surrounding neighborhood.</p>

          <p>There was a lot I had to figure out as I went. Things I was doing by hand I eventually automated, and I worked with High Grounds to clean up how the process worked on their end. Hearing from neighbors who used it and were excited about it really kept me going.</p>

          <p>Getting more businesses on board has been the hardest part. Local business owners are busy, cautious about new things, and already have a lot on their plate. It's slow going and honestly something I haven't fully figured out yet. But the service runs, it's profitable, and it's helped protect over 500 deliveries for people in my community which is what it is all about.</p>
        </div>

        <div className="pp-collage">
          <img src={image1} alt="Porchpair" className="pp-collage-img" />
          <img src={image2} alt="Porchpair" className="pp-collage-img" />
          <img src={image3} alt="Porchpair" className="pp-collage-img" />
        </div>
      </div>
    </div>
  );
}

export default PorchpairPage;
